import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import { useWallets } from "../hooks/useWallets";
import { usePaymentMethods } from "../hooks/usePaymentMethods";
import { useAuth } from "./AuthContext";
import walletService from "../services/walletService";

// Payment method types
export type PaymentMethodType = "card" | "bank";

export interface PaymentMethod {
  id: string;
  type: PaymentMethodType;
  name: string;
  last4: string;
  expiryDate?: string;
  isDefault: boolean;
}

// Transaction types
export type TransactionType =
  | "deposit"
  | "withdrawal"
  | "payment"
  | "refund"
  | "transfer"
  | "all";
export type TransactionStatus = "completed" | "pending" | "failed";

export interface Transaction {
  id: string;
  type: TransactionType;
  amount: number;
  date: string;
  description: string;
  status: TransactionStatus;
  reference?: string;
  paymentMethodId?: string;
}

interface WalletContextType {
  balance: number;
  transactions: Transaction[];
  paymentMethods: PaymentMethod[];
  addFunds: (amount: number, paymentMethodId?: string) => Promise<void>;
  withdrawFunds: (amount: number, paymentMethodId?: string) => Promise<void>;
  makePayment: (
    amount: number,
    description: string,
    reference?: string
  ) => Promise<void>;
  processRefund: (
    amount: number,
    description: string,
    reference?: string
  ) => Promise<void>;
  addPaymentMethod: (method: Omit<PaymentMethod, "id">) => Promise<void>;
  removePaymentMethod: (id: string) => Promise<void>;
  setDefaultPaymentMethod: (id: string) => Promise<void>;
  clearTransactionHistory: () => Promise<void>;
  fetchTransactions: () => Promise<void>;
  isLoading: boolean;
  isError: boolean;
}

const WalletContext = createContext<WalletContextType | undefined>(undefined);

export const WalletProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [balance, setBalance] = useState<number>(0);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  
  // Get authentication state
  const { isAuthenticated } = useAuth();

  // Only use hooks when user is authenticated
  const walletsHook = useWallets();
  const paymentMethodsHook = usePaymentMethods();
  
  // Extract values conditionally
  const {
    wallets,
    addFunds: apiAddFunds,
    withdrawFunds: apiWithdrawFunds,
    isLoading: isWalletsLoading,
    isError: isWalletsError,
  } = isAuthenticated ? walletsHook : {
    wallets: [],
    addFunds: () => {},
    withdrawFunds: () => {},
    isLoading: false,
    isError: false,
  };

  const {
    paymentMethods: apiPaymentMethods,
    createPaymentMethod, // eslint-disable-line @typescript-eslint/no-unused-vars
    deletePaymentMethod,
    setDefaultPaymentMethod: apiSetDefaultPaymentMethod,
    isLoading: isPaymentMethodsLoading,
    isError: isPaymentMethodsError,
  } = isAuthenticated ? paymentMethodsHook : {
    paymentMethods: [],
    createPaymentMethod: () => {},
    deletePaymentMethod: () => {},
    setDefaultPaymentMethod: () => {},
    isLoading: false,
    isError: false,
  };

  // Convert API payment methods to our format
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);

  // Add a ref to track previous payment methods
  const prevApiPaymentMethodsRef = useRef<any>(null);

  // Load data from API when available
  useEffect(() => {
    if (isAuthenticated && wallets && Array.isArray(wallets) && wallets.length > 0) {
      // Sum up balances from all wallets
      const totalBalance = (wallets as any[]).reduce((sum: number, wallet: any) => {
        return sum + parseFloat(wallet.balance);
      }, 0);

      // Only update the balance if it's actually different to prevent loops
      if (totalBalance !== balance) {
        setBalance(totalBalance);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallets, isAuthenticated]);

  // Transform API payment methods to our format - completely rewrite this effect
  useEffect(() => {
    // Skip if the API data hasn't changed
    if (
      prevApiPaymentMethodsRef.current === apiPaymentMethods &&
      apiPaymentMethods &&
      apiPaymentMethods.length > 0
    ) {
      return;
    }

    // Update our ref with the current API data
    prevApiPaymentMethodsRef.current = apiPaymentMethods;

    // Process API payment methods if they exist
    if (apiPaymentMethods && apiPaymentMethods.length > 0) {

      const formattedPaymentMethods = apiPaymentMethods.map((pm) => ({
        id: pm.id.toString(),
        type: pm.type === "card" ? "card" : ("bank" as PaymentMethodType),
        name: pm.name,
        last4: pm.card_last_four,
        expiryDate:
          pm.card_expiry_month && pm.card_expiry_year
            ? `${pm.card_expiry_month}/${pm.card_expiry_year}`
            : undefined,
        isDefault: pm.is_default,
      }));

      setPaymentMethods(formattedPaymentMethods);
    }
  }, [apiPaymentMethods, isPaymentMethodsLoading, isPaymentMethodsError]);

  // Load wallet transactions from API when wallets are loaded
  useEffect(() => {
    if (wallets && wallets.length > 0) {
      fetchTransactions(wallets[0].id);
    }
  }, [wallets]);

  // Load wallet transactions from API
  const fetchTransactions = async (walletId: number) => {
    try {
      setIsLoadingTransactions(true);
      const result = await walletService.getWalletTransactions(walletId);

      if (result?.data?.data) {
        const apiTransactions = result.data.data;

        // Convert API transactions to our format
        const formattedTransactions = apiTransactions.map((tx) => ({
          id: tx.id.toString(),
          type: (tx.type || "payment").toLowerCase() as TransactionType,
          amount: parseFloat(tx.amount),
          date: tx.created_at,
          description: tx.description || `Transaction ${tx.id}`,
          status: (tx.status || "completed").toLowerCase() as TransactionStatus,
          reference: tx.reference_id?.toString() || undefined,
        }));

        setTransactions(formattedTransactions);
      }
    } catch (err) {
      console.error("Error fetching transactions:", err);
    } finally {
      setIsLoadingTransactions(false);
    }
  };

  // Add a ref to track previous transactions
  const prevTransactionsRef = useRef<Transaction[]>([]);

  // Separate useEffect to save transactions to localStorage
  // This was likely causing the infinite loop
  useEffect(() => {
    // Skip if transactions haven't changed
    if (
      transactions.length === 0 ||
      JSON.stringify(transactions) ===
        JSON.stringify(prevTransactionsRef.current)
    ) {
      return;
    }

    // Update the ref
    prevTransactionsRef.current = [...transactions];

    // Save to localStorage
    localStorage.setItem("wallet_transactions", JSON.stringify(transactions));
  }, [transactions]);

  const generateTransactionId = () => {
    return `tx-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
  };

  const addFunds = async (amount: number, paymentMethodId?: string) => {
    if (amount <= 0) throw new Error("Deposit amount must be positive");

    // Find the default payment method if none specified
    let methodId = paymentMethodId;
    if (!methodId) {
      const defaultMethod = paymentMethods.find((m) => m.isDefault);
      methodId = defaultMethod?.id;
    }

    if (!methodId) {
      throw new Error("No payment method available");
    }

    try {
      console.log(
        `Adding funds: $${amount} using payment method ID: ${methodId}`
      );

      // Convert to number if it's a string ID
      const apiMethodId = parseInt(methodId);

      if (isNaN(apiMethodId)) {
        throw new Error("Invalid payment method ID");
      }

      // Prepare the request data
      const fundData = {
        amount: amount,
        currency_code: localStorage.getItem("app_currency") || "ILS",
        payment_method_id: apiMethodId,
      };

      // Call the API to add funds using the mutation
      const result = await new Promise<any>((resolve, reject) => {
        apiAddFunds(fundData, {
          onSuccess: (data) => {
            console.log("Add funds success:", data);
            resolve(data);
          },
          onError: (error) => {
            console.error("Add funds error:", error);
            reject(error);
          },
        });
      });

      // Create a transaction record based on the API response data
      let newTransaction: Transaction;

      if (result && result.data && result.data.transaction) {
        // Use API transaction data
        const apiTx = result.data.transaction;
        newTransaction = {
          id: apiTx.id.toString(),
          type: "deposit",
          amount: parseFloat(apiTx.amount),
          date: apiTx.created_at,
          description:
            apiTx.description || `Add funds via payment method ${methodId}`,
          status: apiTx.status as TransactionStatus,
          paymentMethodId: methodId,
        };
      } else {
        // Fallback to creating our own transaction record
        newTransaction = {
          id: generateTransactionId(),
          type: "deposit",
          amount: amount,
          date: new Date().toISOString(),
          description: `Add funds ${
            methodId
              ? `via ${
                  methodId.includes("card") ? "Credit Card" : "Bank Transfer"
                }`
              : ""
          }`,
          status: "completed",
          paymentMethodId: methodId,
        };
      }

      // Update wallet balance from API response if available
      if (result && result.data && result.data.wallet) {
        const newBalance = parseFloat(result.data.wallet.balance);
        setBalance(newBalance);
      } else {
        // Fallback to local balance calculation
        setBalance((prevBalance) => prevBalance + amount);
      }

      // Update transactions list
      setTransactions((prevTransactions) => [
        newTransaction,
        ...prevTransactions,
      ]);

      return Promise.resolve();
    } catch (error: any) {
      console.error("Error adding funds:", error);

      // Extract error message from API response if available
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "An error occurred while adding funds";

      throw new Error(errorMessage);
    }
  };

  const withdrawFunds = async (amount: number, paymentMethodId?: string) => {
    if (amount <= 0) throw new Error("Withdrawal amount must be positive");
    if (amount > balance) throw new Error("Insufficient funds");

    // Find the default payment method if none specified
    let methodId = paymentMethodId;
    if (!methodId) {
      const defaultMethod = paymentMethods.find((m) => m.isDefault);
      methodId = defaultMethod?.id;
    }

    try {
      if (!methodId) {
        throw new Error("No payment method available");
      }

      // Find default wallet (first one for now)
      const defaultWallet = wallets && wallets.length > 0 ? wallets[0] : null;

      if (!defaultWallet) {
        throw new Error("No wallet available");
      }

      // Call the API to withdraw funds
      await apiWithdrawFunds({
        wallet_id: defaultWallet.id,
        amount: amount,
        metadata: {
          payment_method_id: methodId,
        },
      });

      // Create a local transaction record
      const newTransaction: Transaction = {
        id: generateTransactionId(),
        type: "withdrawal",
        amount: -amount, // Negative amount for outgoing money
        date: new Date().toISOString(),
        description: `Withdrawal to ${
          methodId
            ? `${
                methodId.includes("card") ? "Credit Card" : "Bank Account"
              } ****${
                paymentMethods.find((m) => m.id === methodId)?.last4 || ""
              }`
            : "account"
        }`,
        status: "pending", // Withdrawals are initially pending
        paymentMethodId: methodId,
      };

      // Update local state
      setBalance((prevBalance) => prevBalance - amount);
      setTransactions((prevTransactions) => [
        newTransaction,
        ...prevTransactions,
      ]);

      // Simulate updating the status to completed after some time (for demo purposes)
      setTimeout(() => {
        setTransactions((prevTransactions) =>
          prevTransactions.map((tx) =>
            tx.id === newTransaction.id ? { ...tx, status: "completed" } : tx
          )
        );
      }, 30000); // 30 seconds for demo

      return Promise.resolve();
    } catch (error) {
      throw error;
    }
  };

  const makePayment = async (
    amount: number,
    description: string,
    reference?: string
  ) => {
    if (amount <= 0) throw new Error("Payment amount must be positive");
    if (amount > balance) throw new Error("Insufficient funds");

    const newTransaction: Transaction = {
      id: generateTransactionId(),
      type: "payment",
      amount: -amount, // Negative amount for outgoing money
      date: new Date().toISOString(),
      description,
      status: "completed",
      reference,
    };

    setBalance((prevBalance) => prevBalance - amount);
    setTransactions((prevTransactions) => [
      newTransaction,
      ...prevTransactions,
    ]);

    return Promise.resolve();
  };

  const processRefund = async (
    amount: number,
    description: string,
    reference?: string
  ) => {
    if (amount <= 0) throw new Error("Refund amount must be positive");

    const newTransaction: Transaction = {
      id: generateTransactionId(),
      type: "refund",
      amount: amount, // Positive amount for incoming money
      date: new Date().toISOString(),
      description,
      status: "completed",
      reference,
    };

    setBalance((prevBalance) => prevBalance + amount);
    setTransactions((prevTransactions) => [
      newTransaction,
      ...prevTransactions,
    ]);

    return Promise.resolve();
  };

  const addPaymentMethod = async (method: Omit<PaymentMethod, "id">) => {
    try {
      // For card type, we'll use Stripe
      if (method.type === "card") {
        const newMethod: PaymentMethod = {
          ...method,
          id: `${method.type}-${Date.now()}`,
        };

        // Update local state
        if (method.isDefault || paymentMethods.length === 0) {
          setPaymentMethods((prevMethods) => [
            ...prevMethods.map((m) => ({ ...m, isDefault: false })),
            newMethod,
          ]);
        } else {
          setPaymentMethods((prevMethods) => [...prevMethods, newMethod]);
        }
      } else {
        // For bank accounts
        const newMethod: PaymentMethod = {
          ...method,
          id: `${method.type}-${Date.now()}`,
        };

        // Update local state
        if (method.isDefault || paymentMethods.length === 0) {
          setPaymentMethods((prevMethods) => [
            ...prevMethods.map((m) => ({ ...m, isDefault: false })),
            newMethod,
          ]);
        } else {
          setPaymentMethods((prevMethods) => [...prevMethods, newMethod]);
        }
      }

      return Promise.resolve();
    } catch (error) {
      throw error;
    }
  };

  const removePaymentMethod = async (id: string) => {
    try {
      // Call API to remove the payment method
      if (id.match(/^\d+$/)) {
        // If it's a numeric ID, it's likely from the API
        await deletePaymentMethod(parseInt(id));
      }

      const methodToRemove = paymentMethods.find((m) => m.id === id);

      if (!methodToRemove) {
        throw new Error("Payment method not found");
      }

      // If we're removing the default method, select a new default
      if (methodToRemove.isDefault) {
        const remainingMethods = paymentMethods.filter((m) => m.id !== id);

        if (remainingMethods.length > 0) {
          // Make the first remaining method the default
          setPaymentMethods([
            { ...remainingMethods[0], isDefault: true },
            ...remainingMethods.slice(1),
          ]);
        } else {
          setPaymentMethods([]);
        }
      } else {
        // Just remove the method
        setPaymentMethods((prevMethods) =>
          prevMethods.filter((m) => m.id !== id)
        );
      }

      return Promise.resolve();
    } catch (error) {
      throw error;
    }
  };

  const setDefaultPaymentMethod = async (id: string) => {
    try {
      // Call API to set default payment method
      if (id.match(/^\d+$/)) {
        // If it's a numeric ID, it's likely from the API
        await apiSetDefaultPaymentMethod(parseInt(id));
      }

      // Update local state
      setPaymentMethods((prevMethods) =>
        prevMethods.map((method) => ({
          ...method,
          isDefault: method.id === id,
        }))
      );

      return Promise.resolve();
    } catch (error) {
      throw error;
    }
  };

  const clearTransactionHistory = async () => {
    setTransactions([]);
    return Promise.resolve();
  };

  // Value object for the context
  const value: WalletContextType = {
    balance,
    transactions,
    paymentMethods,
    addFunds,
    withdrawFunds,
    makePayment,
    processRefund,
    addPaymentMethod,
    removePaymentMethod,
    setDefaultPaymentMethod,
    clearTransactionHistory,
    fetchTransactions: () => {
      if (wallets && wallets.length > 0) {
        return fetchTransactions(wallets[0].id);
      }
      return Promise.resolve();
    },
    isLoading:
      isWalletsLoading || isPaymentMethodsLoading || isLoadingTransactions,
    isError: isWalletsError || isPaymentMethodsError,
  };

  return (
    <WalletContext.Provider value={value}>{children}</WalletContext.Provider>
  );
};

export const useWallet = (): WalletContextType => {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error("useWallet must be used within a WalletProvider");
  }
  return context;
};
